import { getAssetUrl } from '@shared/constants';
import { SoundCategory } from '@shared/data/sounds';
import {
  getAudioManager,
  applyPersistedAudioConfig,
  isMusicEnabledPersisted,
  persistMusicEnabled,
} from './sharedAudio';

/**
 * Música de la fase de boot/login (antes de que exista GameScene): arranca la **intro** apenas el
 * usuario hace el primer gesto (los browsers no dejan sonar audio sin interacción), monta el **botón
 * flotante de música** abajo a la derecha (toggle que persiste el canal Música de Opciones) y permite
 * a la creación de personaje cambiar de pista con crossfade (`setLoadingPhaseMusic`).
 *
 * Usa la instancia COMPARTIDA de `AudioManager` (ver `sharedAudio.ts`), así al entrar al mundo la
 * música de zona crossfadea desde la intro sin cortes. Ver docs/SOUND_AND_MUSIC.md.
 */

const INTRO_MUSIC = 'music_intro';

/**
 * Eventos que el browser reconoce como "gesto de usuario" para reanudar un AudioContext (lista de la
 * política de autoplay de Chrome). Escuchamos TODOS para arrancar la música en la micro-interacción
 * más temprana posible (no esperar al click de login). Ver docs/SOUND_AND_MUSIC.md.
 */
const GESTURE_EVENTS = [
  'pointerdown',
  'mousedown',
  'pointerup',
  'mouseup',
  'touchend',
  'keydown',
  'keyup',
  'click',
] as const;

let mounted = false;
let configApplied = false;
let phaseMusicId: string | null = INTRO_MUSIC;
let button: HTMLButtonElement | null = null;
let gestureHandler: (() => void) | null = null;

/** Arranca (o reanuda) la música de la fase actual si el contexto suena y el canal está habilitado. */
function playPhaseIfEnabled(): void {
  const mgr = getAudioManager();
  if (!mgr.isRunning() || !phaseMusicId) return;
  if (mgr.getConfig().categories[SoundCategory.MUSIC]?.enabled) {
    mgr.playMusic(phaseMusicId);
  }
}

/** Quita los listeners de gesto (ya no hacen falta una vez que el contexto arrancó). */
function removeGestureListeners(): void {
  if (!gestureHandler) return;
  for (const ev of GESTURE_EVENTS) window.removeEventListener(ev, gestureHandler);
  gestureHandler = null;
}

/**
 * Crea/reanuda el AudioContext y, SÓLO si quedó realmente corriendo, arranca la música de la fase.
 * En el boot (sin gesto) el browser deja el contexto suspendido → no hace nada y espera el primer
 * gesto; en ese gesto `resume()` prospera, el contexto pasa a 'running' y recién ahí suena. Es
 * idempotente y se puede llamar tantas veces como haga falta. NO encola música en un contexto
 * suspendido (eso hacía que el sonido apareciera recién al reanudarse en un click posterior).
 */
async function tryUnlockAndPlay(): Promise<void> {
  const mgr = getAudioManager();
  await mgr.unlock();
  if (!mgr.isRunning()) return; // todavía bloqueado por el browser: esperamos un gesto real
  if (!configApplied) {
    applyPersistedAudioConfig(mgr);
    configApplied = true;
  }
  playPhaseIfEnabled();
  removeGestureListeners(); // el contexto ya suena: no hace falta seguir escuchando gestos
}

function updateButton(): void {
  if (!button) return;
  const enabled = isMusicEnabledPersisted();
  button.textContent = '🎵';
  button.style.opacity = enabled ? '1' : '0.4';
  button.title = enabled ? 'Música: activada (click para silenciar)' : 'Música: silenciada (click para activar)';
  button.setAttribute('aria-pressed', String(enabled));
}

function createButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'aureum-loading-music-btn';
  Object.assign(btn.style, {
    position: 'fixed',
    right: '16px',
    bottom: '16px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '1px solid rgba(200,170,110,0.5)',
    background: 'rgba(20,16,10,0.75)',
    color: '#e8dcc0',
    fontSize: '20px',
    lineHeight: '42px',
    textAlign: 'center',
    cursor: 'pointer',
    zIndex: '100000',
    padding: '0',
    userSelect: 'none',
  } as Partial<CSSStyleDeclaration>);

  btn.addEventListener('click', () => {
    const mgr = getAudioManager();
    const nowEnabled = !(mgr.getConfig().categories[SoundCategory.MUSIC]?.enabled ?? true);
    mgr.setCategoryEnabled(SoundCategory.MUSIC, nowEnabled);
    persistMusicEnabled(nowEnabled);
    // El click es un gesto válido: desbloquea el contexto y arranca la música si quedó habilitada.
    if (nowEnabled) void tryUnlockAndPlay();
    updateButton();
  });

  return btn;
}

/**
 * Monta la música de la fase de carga. Llamar una vez apenas arranca `bootstrap()`.
 */
export function mountLoadingMusic(): void {
  if (mounted || typeof window === 'undefined') return;
  mounted = true;

  // Calentar la caché HTTP de la intro cuanto antes (la decodificación real ocurre al desbloquear).
  // `priority: 'high'` (Fetch Priority API) la pone al frente de la cola de descargas: es lo primero
  // que baja el cliente apenas arranca, así el .ogg ya está en caché cuando el usuario da el primer
  // gesto y la música suena sin esperar la red. `mountLoadingMusic` se llama al inicio de bootstrap().
  try {
    void fetch(getAssetUrl('audio/music/intro.ogg'), {
      priority: 'high',
    } as RequestInit & { priority: 'high' });
  } catch {
    /* noop */
  }

  // Desbloqueo en el primer gesto del usuario (requisito de autoplay del browser). NO son `once`:
  // `tryUnlockAndPlay` los saca por su cuenta recién cuando el contexto quedó realmente sonando, así
  // un gesto que no alcanza a reanudarlo no consume el listener y el siguiente vuelve a intentar.
  gestureHandler = (): void => void tryUnlockAndPlay();
  for (const ev of GESTURE_EVENTS) window.addEventListener(ev, gestureHandler);

  // Botón flotante.
  button = createButton();
  document.body.appendChild(button);
  updateButton();

  // Intento OPTIMISTA de arrancar ya (junto con el logo del loading): crea el contexto e intenta
  // reanudarlo. Si el browser permite autoplay (usuario recurrente / PWA con engagement) suena de
  // una durante la carga; si no, el contexto queda suspendido, NO se encola música, y arranca en el
  // primer gesto (listeners de arriba). No hay forma de garantizar sonido sin interacción — es
  // política del browser. Ver docs/SOUND_AND_MUSIC.md.
  void tryUnlockAndPlay();
}

/**
 * Fija la pista de la fase actual (login vs creación de personaje) y hace crossfade a ella. Se usa
 * para volver a la intro al salir de la creación, o poner `music_createchar` al entrar.
 */
export function setLoadingPhaseMusic(id: string): void {
  phaseMusicId = id;
  playPhaseIfEnabled();
}

/**
 * Muestra/oculta SÓLO el botón flotante de música (sin tocar la reproducción ni los listeners de
 * gesto). El botón vive únicamente en el loading inicial y el login; en el picker/creación de
 * personaje se oculta (el jugador maneja el audio desde Opciones in-game). Ver docs/SOUND_AND_MUSIC.md.
 */
export function setLoadingMusicButtonVisible(visible: boolean): void {
  if (button) button.style.display = visible ? '' : 'none';
}

/**
 * Desmonta el botón + listeners de la fase de carga. NO detiene la música: el juego (GameScene) toma
 * la instancia compartida y crossfadea a la música de zona. Llamar en `game:ready`.
 */
export function unmountLoadingMusic(): void {
  if (button) {
    button.remove();
    button = null;
  }
  removeGestureListeners();
  mounted = false;
}
